import { useState, useRef, useCallback, useId, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  /** Tooltip content (string or JSX) */
  content: React.ReactNode;
  /** Position relative to the trigger element */
  position?: TooltipPosition;
  /** Show delay in ms (default 200) */
  delay?: number;
  /** The trigger element(s) */
  children: React.ReactNode;
  /** Additional CSS classes for the tooltip bubble */
  className?: string;
}

const arrowClasses: Record<TooltipPosition, string> = {
  top: 'border-t-bg-elevated border-x-transparent border-b-transparent',
  bottom: 'border-b-bg-elevated border-x-transparent border-t-transparent',
  left: 'border-l-bg-elevated border-y-transparent border-r-transparent',
  right: 'border-r-bg-elevated border-y-transparent border-l-transparent',
};

/**
 * Accessible floating tooltip that appears on hover / keyboard focus.
 *
 * Uses `aria-describedby` so screen readers associate the tooltip
 * content with the trigger element.
 *
 * Portal-rendered to document.body so it escapes overflow containers
 * and never adds scrollbars to parent elements.
 */
export function Tooltip({
  content,
  position = 'top',
  delay = 200,
  children,
  className,
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();

  // Track tooltip position computed from trigger bounding rect
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [arrowOffset, setArrowOffset] = useState<{ top?: string; left?: string }>({});

  const computePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tip = tooltipRef.current;
    if (!trigger || !tip) return;

    const tRect = trigger.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();
    const GAP = 8;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = tRect.top - tipRect.height - GAP;
        left = tRect.left + tRect.width / 2 - tipRect.width / 2;
        break;
      case 'bottom':
        top = tRect.bottom + GAP;
        left = tRect.left + tRect.width / 2 - tipRect.width / 2;
        break;
      case 'left':
        top = tRect.top + tRect.height / 2 - tipRect.height / 2;
        left = tRect.left - tipRect.width - GAP;
        break;
      case 'right':
        top = tRect.top + tRect.height / 2 - tipRect.height / 2;
        left = tRect.right + GAP;
        break;
    }

    // Clamp to viewport so the tooltip is always fully visible
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const MARGIN = 6;
    const clampedLeft = Math.max(MARGIN, Math.min(left, vw - tipRect.width - MARGIN));
    const clampedTop = Math.max(MARGIN, Math.min(top, vh - tipRect.height - MARGIN));

    // Compute arrow offset when clamping shifted the tooltip horizontally
    const newArrowOffset: { top?: string; left?: string } = {};
    if (position === 'top' || position === 'bottom') {
      const shift = left - clampedLeft;
      if (Math.abs(shift) > 1) {
        newArrowOffset.left = `calc(50% + ${shift}px)`;
      }
    } else {
      const shift = top - clampedTop;
      if (Math.abs(shift) > 1) {
        newArrowOffset.top = `calc(50% + ${shift}px)`;
      }
    }

    setCoords({ top: clampedTop, left: clampedLeft });
    setArrowOffset(newArrowOffset);
  }, [position]);

  // Recompute position on layout after showing
  useLayoutEffect(() => {
    if (visible) computePosition();
  }, [visible, computePosition]);

  const show = useCallback(() => {
    timeoutRef.current = setTimeout(() => setVisible(true), delay);
  }, [delay]);

  const hide = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setVisible(false);
  }, []);

  const arrowPositionStyle: React.CSSProperties = {};
  if (position === 'top') {
    arrowPositionStyle.bottom = '-8px';
    arrowPositionStyle.left = arrowOffset.left ?? '50%';
    arrowPositionStyle.transform = 'translateX(-50%)';
  } else if (position === 'bottom') {
    arrowPositionStyle.top = '-8px';
    arrowPositionStyle.left = arrowOffset.left ?? '50%';
    arrowPositionStyle.transform = 'translateX(-50%)';
  } else if (position === 'left') {
    arrowPositionStyle.right = '-8px';
    arrowPositionStyle.top = arrowOffset.top ?? '50%';
    arrowPositionStyle.transform = 'translateY(-50%)';
  } else if (position === 'right') {
    arrowPositionStyle.left = '-8px';
    arrowPositionStyle.top = arrowOffset.top ?? '50%';
    arrowPositionStyle.transform = 'translateY(-50%)';
  }

  return (
    <div
      ref={triggerRef}
      className="relative inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      aria-describedby={visible ? tooltipId : undefined}
    >
      {children}
      {visible && content && createPortal(
        <div
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            zIndex: 99999,
          }}
          className={cn(
            'pointer-events-none',
            'px-2.5 py-1.5 rounded-lg text-xs font-medium',
            // Below lg a long label wraps inside a capped width (viewport clamp only shifts it); from lg it stays on one line
            'w-max max-w-xs whitespace-normal lg:max-w-none lg:whitespace-nowrap',
            'bg-bg-elevated text-text-primary shadow-lg border border-border-subtle',
            'animate-fade-in-up',
            className,
          )}
        >
          {content}
          <span
            className={cn(
              'absolute w-0 h-0 border-[4px]',
              arrowClasses[position],
            )}
            style={arrowPositionStyle}
            aria-hidden="true"
          />
        </div>,
        document.body,
      )}
    </div>
  );
}

export default Tooltip;
